/**
 * @fileoverview ClubOS — main application shell
 * Handles routing, theme, RTL support, Sidenav rendering, and auth guards.
 */

import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import SoftBox from "components/SoftBox";
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

import routes from "routes";
import { useSoftUIController, setMiniSidenav } from "context";
import { useAuth } from "context/AuthContext";
import { ProtectedRoute } from "components/ProtectedRoute";
import { CLUB_NAME } from "utils/indiaLocale";

const brand = `${process.env.PUBLIC_URL}/images/Athelite.png`;
void SoftBox;

// Auth routes that do NOT need a guard (sign-in, sign-up)
const AUTH_ROUTES = ["/authentication/sign-in", "/authentication/sign-up"];

export default function App() {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, direction, layout, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  const { isAuthenticated, loading } = useAuth();

  useMemo(() => {
    setRtlCache(createCache({ key: "rtl", stylisPlugins: [rtlPlugin] }));
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) { setMiniSidenav(dispatch, false); setOnMouseEnter(true); }
  };
  const handleOnMouseLeave = () => {
    if (onMouseEnter) { setMiniSidenav(dispatch, true); setOnMouseEnter(false); }
  };

  useEffect(() => { document.body.setAttribute("dir", direction); }, [direction]);
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  /**
   * Build <Route> elements. Public auth routes render directly.
   * All other routes are wrapped in <ProtectedRoute> with optional permission.
   */
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) return getRoutes(route.collapse);
      if (!route.route) return null;

      const isAuthRoute = AUTH_ROUTES.includes(route.route);

      if (isAuthRoute) {
        // Auth pages: redirect to dashboard if already logged in
        return (
          <Route
            exact
            path={route.route}
            element={
              isAuthenticated
                ? <Navigate to="/academy/dashboard" replace />
                : route.component
            }
            key={route.key}
          />
        );
      }

      // All other routes: require authentication (+ optional permission)
      return (
        <Route
          exact
          path={route.route}
          element={
            <ProtectedRoute permission={route.permission} anyOf={route.anyOf}>
              {route.component}
            </ProtectedRoute>
          }
          key={route.key}
        />
      );
    });

  // While checking the stored token, render nothing to avoid a flash.
  if (loading) return null;

  const sidenavComponent = (
    <Sidenav
      color={sidenavColor}
      brand={brand}
      brandName={CLUB_NAME}
      routes={routes}
      onMouseEnter={handleOnMouseEnter}
      onMouseLeave={handleOnMouseLeave}
    />
  );

  const defaultRedirect = isAuthenticated
    ? <Navigate to="/academy/dashboard" />
    : <Navigate to="/authentication/sign-in" />;

  const content = (
    <>
      {layout === "dashboard" && isAuthenticated && sidenavComponent}
      <Routes>
        {getRoutes(routes)}
        <Route path="/dashboard" element={<Navigate to="/academy/dashboard" />} />
        <Route path="*" element={defaultRedirect} />
      </Routes>
    </>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={themeRTL}>
        <CssBaseline />
        {content}
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {content}
    </ThemeProvider>
  );
}
